import React, { useCallback, useEffect, useState } from 'react';
import { UserRound, Trash2 } from 'lucide-react';
import Overlay from './PlannerOverlay';
import { getPlannerIndividual } from '../../api';
import { addDays, fmtDayShort } from './plannerShared';

// Individual schedules: take a player off the team schedule for a period. On those days the
// player only sees blocks made for them (in their own view of the week); team blocks don't reach
// them. Like groups, a change in a published week stays private until the week is published again.

export default function IndividualDialog({ token, players, defaultFrom, canEdit, busy, onClose, onSave, onOpen }) {
  const [periods, setPeriods] = useState(null);
  const [error, setError] = useState(null);
  const [playerId, setPlayerId] = useState('');
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(addDays(defaultFrom, 6));

  const load = useCallback(async () => {
    try { setPeriods((await getPlannerIndividual(token)).periods); setError(null); } catch (e) { setError(e.message); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const save = async body => { if (await onSave(body)) load(); };
  const valid = playerId && from && to && from <= to;
  const nameOf = id => players.find(p => p.id === Number(id))?.name || `#${id}`;

  return (
    <Overlay title="Individual schedules" onClose={onClose} width={560}>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        A player on an individual schedule doesn't see the team's blocks on those days, only the blocks made for them.
        Plan those in the player's own view of the week. Players see the change once the week is published.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Current and upcoming</span>
        {error && <span style={{ fontSize: 12, color: 'var(--pl-err)' }}>{error}</span>}
        {periods && !periods.length && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Nobody is on an individual schedule.</span>}
        {(periods || []).map(p => (
          <div key={`${p.player_id}-${p.from}`} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, border: '1px solid var(--border)', borderRadius: 8, padding: '6px 8px' }}>
            <span style={{ flex: 1, minWidth: 0 }}><strong>{p.name}</strong> · {fmtDayShort(p.from)}{p.to !== p.from ? ` – ${fmtDayShort(p.to)}` : ''}</span>
            <button type="button" className="pl-btn" style={{ minHeight: 26, padding: '2px 8px', fontSize: 12 }} onClick={() => onOpen(p.player_id, p.from)}>
              <UserRound size={13} /> Open
            </button>
            {canEdit && (
              <button type="button" className="pl-btn" disabled={busy} aria-label={`Put ${p.name} back on the team schedule`} style={{ minHeight: 26, padding: '2px 8px', fontSize: 12 }}
                onClick={() => window.confirm(`Put ${p.name} back on the team schedule for ${fmtDayShort(p.from)} – ${fmtDayShort(p.to)}? The blocks made for ${p.name} stay; they are just shown next to the team's again.`)
                  && save({ player_id: p.player_id, from: p.from, to: p.to, on: false })}>
                <Trash2 size={13} /> End
              </button>
            )}
          </div>
        ))}
      </div>

      {canEdit && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Set a period</span>
          <label className="pl-label">Player
            <select className="pl-input" value={playerId} onChange={e => setPlayerId(e.target.value)}>
              <option value="">Choose a player…</option>
              {players.map(p => <option key={p.id} value={p.id}>{p.squad_number != null ? `${p.squad_number} · ` : ''}{p.name}</option>)}
            </select>
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <label className="pl-label" style={{ flex: 1 }}>From<input className="pl-input" type="date" value={from} onChange={e => setFrom(e.target.value)} /></label>
            <label className="pl-label" style={{ flex: 1 }}>To (last day)<input className="pl-input" type="date" value={to} onChange={e => setTo(e.target.value)} /></label>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="pl-btn pl-btn--primary" disabled={busy || !valid}
              onClick={() => save({ player_id: Number(playerId), from, to, on: true })}>
              Individual schedule for these days
            </button>
            <button type="button" className="pl-btn" disabled={busy || !valid}
              onClick={() => save({ player_id: Number(playerId), from, to, on: false })}>
              Back on the team schedule for these days
            </button>
          </div>
          {valid && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{nameOf(playerId)}: {fmtDayShort(from)} – {fmtDayShort(to)}</span>}
        </div>
      )}
    </Overlay>
  );
}
